/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {Box, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import Link from '@salesforce/retail-react-app/app/components/link'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useSegment from '../../../integrations/segment/app/hooks/use-segment'
import ProductCarousel from '../../../../app/components/product-carousel'
import {productCarouselStyles} from '../../../theme/components/project/product-carousel'
import textTileWithProductilesContainer from '../../../theme/components/project/textTileWithProductilesContainer'
import {getPDPath} from '../../../utils/custom-utils'

/**
 * Text tile with einstein carousel
 *
 * @param {object} props
 * @returns {React.ReactElement} - TextTileWithEinsteinCarousel component.
 */
export const TextTileWithEinsteinCarousel = ({
    backgroundImage,
    title,
    titleTextColor,
    titleTextStyle,
    titleTextFontWeight,
    subTitle,
    subTitleTextColor,
    subTitleTextStyle,
    subTitleTextFontWeight,
    tileText,
    tileTextColor,
    tileTextStyle,
    tileTextFontWeight,
    tileUrl,
    recommender
}) => {
    const {site, locale} = useMultiSite()
    const categoryUrl = getPDPath(tileUrl, site, locale)
    const styles = useMultiStyleConfig('TrendingProducts')
    const segment = useSegment()
    return (
        <Box sx={textTileWithProductilesContainer}>
            <Box
                {...styles.trendingProducts}
                className="textTileWithProduct-container"
                backgroundImage={backgroundImage.url}
                backgroundSize="cover"
                backgroundPosition="center"
            >
                {/* Text Tile */}
                <Box className="textTile-main-block" {...styles.trendingProductsWrapper}>
                    <Box className="textTile-inner-block">
                        {/* Heading */}
                        {title && (
                            <Text
                                as="h2"
                                className="heading"
                                color={titleTextColor?.value || 'inherit'}
                                fontStyle={titleTextStyle || 'normal'}
                                fontWeight={titleTextFontWeight || 'normal'}
                                {...styles.heading}
                            >
                                {title}
                            </Text>
                        )}
                        {/* Sub Title */}
                        {subTitle && (
                            <Text
                                className="textDescription"
                                color={subTitleTextColor?.value || 'inherit'}
                                fontStyle={subTitleTextStyle || 'normal'}
                                fontWeight={subTitleTextFontWeight || 'normal'}
                                {...styles.textDescription}
                            >
                                {subTitle}
                            </Text>
                        )}
                        {tileText && (
                            <Box className="link-block">
                                <Link
                                    to={categoryUrl}
                                    color={tileTextColor?.value || ''}
                                    fontStyle={tileTextStyle || ''}
                                    fontWeight={tileTextFontWeight || ''}
                                    backgroundImage={`url(${getAssetUrl(
                                        'static/img/icon-arrow-right-white.svg'
                                    )})`}
                                    sx={{...styles.linkStyle, ...styles.link}}
                                    onClick={(e) => {
                                        // segment
                                        segment.sendPanelSelectedEvent(e, {})
                                    }}
                                >
                                    {tileText}
                                </Link>
                            </Box>
                        )}
                    </Box>
                </Box>
                {/* Einstein Carousel */}
                <Box className="textTile-products-block" {...styles.scrollerBlock}>
                    <Box className="apparel-product-tile-carousel" sx={productCarouselStyles}>
                        {recommender && recommender.value && (
                            <Box className={'einsteinRecommendations'}>
                                <ProductCarousel
                                    recommender={recommender.value}
                                    isApparelProducts={true}
                                />
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

TextTileWithEinsteinCarousel.propTypes = {
    backgroundImage: PropTypes.shape({
        _type: PropTypes.string,
        focalPoint: PropTypes.shape({
            _type: PropTypes.string,
            x: PropTypes.number,
            y: PropTypes.number
        }),
        metaData: PropTypes.shape({
            _type: PropTypes.string,
            height: PropTypes.number,
            width: PropTypes.number
        }),
        url: PropTypes.string
    }),
    title: PropTypes.string,
    titleTextColor: PropTypes.shape({
        value: PropTypes.string
    }),
    titleTextStyle: PropTypes.string,
    titleTextFontWeight: PropTypes.string,
    subTitle: PropTypes.string,
    subTitleTextColor: PropTypes.shape({
        value: PropTypes.string
    }),
    subTitleTextStyle: PropTypes.string,
    subTitleTextFontWeight: PropTypes.string,
    tileText: PropTypes.string,
    tileTextColor: PropTypes.shape({
        value: PropTypes.string
    }),
    tileTextStyle: PropTypes.string,
    tileTextFontWeight: PropTypes.string,
    tileUrl: PropTypes.string,
    displayRatings: PropTypes.bool,
    recommender: PropTypes.shape({
        value: PropTypes.string
    })
}

export default TextTileWithEinsteinCarousel
